import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Ic } from './Icon';
import { useAuth, isAdmin } from '../store/auth';
import { roomInvitesApi } from '../api/roomInvites';
import { roomNotificationsApi } from '../api/roomNotifications';

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  exact?: boolean;
  badge?: number;
}

export function MobileNav() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();

  const inviteCount = useQuery({
    queryKey: ['my-room-invites-count'],
    queryFn: roomInvitesApi.count,
    refetchInterval: 300_000,
  });
  const notifCount = useQuery({
    queryKey: ['my-room-notifications-count'],
    queryFn: roomNotificationsApi.count,
    refetchInterval: 300_000,
  });
  const pendingInvites = (inviteCount.data?.count ?? 0) + (notifCount.data?.count ?? 0);

  const items: NavItem[] = [
    { to: '/', icon: <Ic.home size={20} />, label: 'Dashboard', exact: true },
    { to: '/calendar', icon: <Ic.calendar size={20} />, label: 'Mecze' },
    { to: '/rooms', icon: <Ic.users size={20} />, label: 'Pokoje', badge: pendingInvites },
    { to: '/profile', icon: <Ic.user size={20} />, label: 'Profil' },
    ...(isAdmin(user)
      ? [{ to: '/admin/matches', icon: <Ic.bolt size={20} />, label: 'Zarządzaj' } as NavItem]
      : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const itemBaseStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    color: isActive ? '#0f1e3d' : 'rgba(15,30,61,.5)',
    fontSize: 10.5,
    fontWeight: isActive ? 600 : 500,
    textDecoration: 'none',
    minWidth: 0,
    padding: '0 2px',
  });

  return (
    <nav
      className="mobile-only"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 64,
        background: '#fff',
        borderTop: '1px solid rgba(15,30,61,.08)',
        display: 'flex',
        zIndex: 100,
      }}
    >
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.exact}
          style={({ isActive }) => itemBaseStyle(isActive)}
        >
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            {it.icon}
            {it.badge && it.badge > 0 ? (
              <span
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -8,
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  borderRadius: 8,
                  background: '#d0463c',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {it.badge}
              </span>
            ) : null}
          </span>
          {it.label}
        </NavLink>
      ))}
      <button
        type="button"
        onClick={handleLogout}
        style={{
          ...itemBaseStyle(false),
          color: '#8a2a22',
          background: 'transparent',
          border: 'none',
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        <Ic.logout size={20} />
        Wyloguj
      </button>
    </nav>
  );
}
